"use client";

/**
 * Request Portal Link Page
 * Allows attendees to request their portal link by entering their email
 * Route: /[slug]/attendee/request-link
 */

import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import LandingPageLayout from "@/components/shared/LandingPageLayout";
import { Loader2, CheckCircle, AlertCircle, ArrowLeft, Mail } from "lucide-react";

export default function RequestPortalLinkPage() {
  // Hooks
  const params = useParams();
  const slug = params.slug as string;

  // States
  const [landingPageId, setLandingPageId] = useState<number | null>(null);
  const [landingPageName, setLandingPageName] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [primaryColor, setPrimaryColor] = useState("#5261EB");
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Effects - fetch landing page info by slug via API (uses service role, avoids anon RLS)
  useEffect(() => {
    const fetchLandingPage = async () => {
      try {
        const response = await fetch(`/api/attendee/request-link?slug=${encodeURIComponent(slug)}`);
        if (!response.ok) {
          setPageError("Landing page not found");
          setPageLoading(false);
          return;
        }
        const data = await response.json();

        setLandingPageId(data.landing_page_id);
        setLandingPageName(data.name || "");

        if (data.event_id === 1236) {
          setLogoUrl(
            "https://vqftsjfwhonwbpyogmzi.supabase.co/storage/v1/object/public/forms/81/2020%20Benchling%20Logo_Gradient%20Wordmark%202.png"
          );
        } else if (data.custom_logo_path) {
          setLogoUrl(data.custom_logo_path);
        } else {
          setLogoUrl("https://www.nowadays.ai/nowadays_black.svg");
        }

        if (data.event_id === 262) {
          setPrimaryColor("#F97316");
        }

        setPageLoading(false);
      } catch (err) {
        console.error("Error fetching landing page:", err);
        setPageError("Failed to load page");
        setPageLoading(false);
      }
    };

    if (slug) {
      fetchLandingPage();
    }
  }, [slug]);

  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!landingPageId) {
      setError("Landing page not found");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/attendee/request-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          landing_page_id: landingPageId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "Something went wrong. Please try again.");
        setIsSubmitting(false);
        return;
      }

      if (result.found === false) {
        setError(
          "No registration found for this email address. If you registered under a different email, please try that instead."
        );
        setIsSubmitting(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Error requesting portal link:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render loading state
  if (pageLoading) {
    return (
      <LandingPageLayout>
        <div className="flex items-center justify-center p-4 min-h-screen">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-black/60" />
            <p className="text-black/80">Loading...</p>
          </div>
        </div>
      </LandingPageLayout>
    );
  }

  // Render page error state
  if (pageError) {
    return (
      <LandingPageLayout>
        <div className="flex items-center justify-center p-4 min-h-screen">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-amber-500" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h1>
            <p className="text-gray-600">{pageError}</p>
          </div>
        </div>
      </LandingPageLayout>
    );
  }

  // Render success state
  if (submitted) {
    return (
      <LandingPageLayout>
        <div className="flex items-center justify-center p-4 min-h-screen">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img src={logoUrl} alt={landingPageName || "Event Logo"} className="h-8 object-contain" />
            </div>
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              If you&apos;re registered for this event, we&apos;ve sent an email to <strong>{email}</strong> with a link
              to your personal portal.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Don&apos;t see it? Check your spam folder or try again with a different email address.
            </p>
            <a
              href={`/${slug}`}
              className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
              style={{ color: primaryColor }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to event
            </a>
          </div>
        </div>
      </LandingPageLayout>
    );
  }

  return (
    <LandingPageLayout>
      <div className="flex items-center justify-center p-4 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          {/* Logo */}
          {logoUrl && (
            <div className="flex justify-center mb-6">
              <img src={logoUrl} alt={landingPageName || "Event Logo"} className="h-8 object-contain" />
            </div>
          )}

          {/* Header */}
          <div className="mb-6">
            <a
              href={`/${slug}`}
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to event
            </a>
            <h1 className="text-xl font-semibold text-gray-900">Access Your Portal</h1>
            {landingPageName && <p className="text-sm text-gray-500 mt-1">{landingPageName}</p>}
            <p className="text-gray-600 mt-2">
              Enter your email address to receive a link to your personal attendee portal where you can view and edit
              your registration details.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  placeholder="your.email@company.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                  disabled={isSubmitting}
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2.5 px-4 rounded-lg font-medium text-white disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Portal Link"
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-gray-500 mt-8">
            Powered by{" "}
            <a href="https://www.nowadays.ai" className="hover:text-gray-700">
              Nowadays
            </a>
          </p>
        </div>
      </div>
    </LandingPageLayout>
  );
}
